import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, Loader2, Eye, EyeOff, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const DeliverySettings = () => {
  const [config, setConfig] = useState({
    enabled:   false,
    fromName:  '',
    fromEmail: '',
    host:      '',
    port:      587,
    secure:    false,
    user:      '',
    pass:      '',
  });
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    api.get('/delivery-settings/email')
      .then(r => setConfig(r.data.data))
      .catch(() => toast.error('Failed to load email settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/delivery-settings/email', config);
      toast.success('Email settings saved');
    } catch (err) {
      console.error('[DeliverySettings] Save error:', err.response?.status, err.response?.data, err.message);
      toast.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/delivery-settings/email/test');
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const set = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-meraki-indigo" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
          <Mail className="w-5 h-5 text-meraki-indigo" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Email Delivery</h2>
          <p className="text-sm text-slate-500">Configure your own SMTP to send hyperpersonalized candidate invites</p>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-6">
        <div>
          <p className="font-semibold text-slate-700 text-sm">Use custom email</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {config.enabled ? 'Invites send from your SMTP' : 'Using system default (careers@hrgptai.in)'}
          </p>
        </div>
        <button
          onClick={() => set('enabled', !config.enabled)}
          className={`w-12 h-6 rounded-full transition-colors relative ${config.enabled ? 'bg-meraki-indigo' : 'bg-slate-300'}`}
        >
          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {config.enabled && (
        <div className="space-y-4">
          {/* From details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">From Name</label>
              <input
                type="text"
                value={config.fromName}
                onChange={e => set('fromName', e.target.value)}
                placeholder="Degree2Destiny HR"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">From Email</label>
              <input
                type="email"
                value={config.fromEmail}
                onChange={e => set('fromEmail', e.target.value)}
                placeholder="hr@degree2destiny.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
              />
            </div>
          </div>

          {/* SMTP */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">SMTP Host</label>
              <input
                type="text"
                value={config.host}
                onChange={e => set('host', e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Port</label>
              <input
                type="number"
                value={config.port}
                onChange={e => set('port', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
              />
            </div>
          </div>

          {/* Credentials */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">SMTP Username</label>
              <input
                type="text"
                value={config.user}
                onChange={e => set('user', e.target.value)}
                placeholder="hr@degree2destiny.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">SMTP Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={config.pass}
                  onChange={e => set('pass', e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 pr-9 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* SSL toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.secure}
              onChange={e => set('secure', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-slate-600">Use SSL (port 465)</span>
          </label>

          {/* Common provider hints */}
          <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700 space-y-1">
            <p className="font-semibold">Common SMTP settings:</p>
            <p>Gmail: smtp.gmail.com · port 587 · use App Password (not your regular password)</p>
            <p>Outlook: smtp.office365.com · port 587</p>
            <p>GoDaddy: smtpout.secureserver.net · port 587</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-meraki-indigo text-white rounded-lg text-sm font-semibold hover:bg-indigo-800 transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save Settings'}
        </button>

        {config.enabled && (
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? 'Sending test…' : 'Send Test Email'}
          </button>
        )}
      </div>
    </div>
  );
};

export default DeliverySettings;
